import { ErrorHandler, Injectable } from '@angular/core';
import { Faro, getWebInstrumentations, initializeFaro, LogLevel, TransportItem } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { environment } from 'src/environments/environment';
import { version as appVersion } from '../../../../package.json';

import { getSessionName } from './session-name';

let faroInstance: Faro | null = null;

// Route/API path shapes that carry a username, email, or free-text search term. Faro records page
// URLs and outgoing request URLs automatically, so these segments are redacted before anything
// leaves the browser - the Faro session id must remain the only identifier in telemetry.
// Group 1 is the preserved path prefix; the segment after it is replaced.
// Everything up to the next path/query/fragment delimiter, closing paren (Angular outlet syntax), or quote.
const SEGMENT = String.raw`[^/?#()"'\s\x5c]+`;
const RESERVED_USER_SEGMENTS = 'bulk|login|create|update|friend|profile|online|valid|autologin|refreshSession|userProfile';
const IDENTITY_PATH_PATTERNS: RegExp[] = [
    new RegExp(String.raw`(/user/valid/(?:username|email)/)${SEGMENT}`, 'gi'),
    new RegExp(String.raw`(/user/friend/)${SEGMENT}`, 'gi'),
    new RegExp(String.raw`(/(?:shop|achievement)/user/)${SEGMENT}`, 'gi'),
    new RegExp(String.raw`(/search/)${SEGMENT}`, 'gi'),
    new RegExp(String.raw`(/mailbox/compose/)${SEGMENT}`, 'gi'),
    // Profile route (/user/:username, /user-interactor/:username inside the modal outlet) and
    // GET /api/user/:username share a shape; the reserved words are that controller's own sub-routes.
    new RegExp(String.raw`([/:]user(?:-interactor)?/)(?!(?:${RESERVED_USER_SEGMENTS})(?:[/?#()]|$))${SEGMENT}`, 'gi'),
];

function scrubIdentityFromUrls<T>(value: T): T {
    if (typeof value === 'string') {
        let scrubbed: string = value;
        for (const pattern of IDENTITY_PATH_PATTERNS) {
            scrubbed = scrubbed.replace(pattern, '$1redacted');
        }
        return scrubbed as unknown as T;
    }
    if (Array.isArray(value)) {
        return value.map(scrubIdentityFromUrls) as unknown as T;
    }
    if (value !== null && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        for (const [key, inner] of Object.entries(value)) {
            result[key] = scrubIdentityFromUrls(inner);
        }
        return result as T;
    }
    return value;
}

function escapeForRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\x5d\x5c]/g, '\x5c$&');
}

// Path with no trailing slash, e.g. "https://critters.io/api" - matches that path or anything
// under it (a following /, ?, or # ), but not an unrelated path that merely starts with the same
// text (e.g. "/apidocs"). Exported so callers outside this file (the session-id HttpInterceptor)
// can match the same API prefix without a second copy of this regex.
export function originPathPrefix(url: URL): string {
    const path = url.pathname.replace(/\/$/, '');
    return `${escapeForRegExp(url.origin)}${escapeForRegExp(path)}(?:[/?#]|$)`;
}

// Faro's fetch/XHR tracing instruments every network call a page makes, including asset loads
// that happen to go through fetch/XHR rather than a plain <img> tag (Phaser's asset loader is
// one such caller) - without this, a game's sprites/sounds/its own JS/CSS bundle outnumber real
// API calls in every trace and log view. `ignoreUrls` is a denylist, so "trace only our own
// backend" is expressed as "ignore anything that is not the API or SignalR" via a negative
// lookahead anchored at the start of the URL - built from the exact URLs HttpClient/SignalR
// themselves target, so it can't miss a real backend call or need updating for a new asset type.
const BACKEND_URL_PREFIXES = [
    originPathPrefix(new URL(environment.apiUrl, window.location.href)),
    originPathPrefix(new URL(environment.signalRBase, window.location.href)),
].join('|');

// Polled every 60s per connected client (inbox.service.ts) regardless of whether anything changed
// - a real backend call, so the allowlist above keeps it, but pure noise on every successful poll.
// Unlike the backend's OnlineFriendsPollSuppressionProcessor, this is a blanket exclusion: Faro's
// ignoreUrls is evaluated before the request completes, so it cannot conditionally keep only the
// failed polls the way a span processor running after the response is known can.
const ONLINE_FRIENDS_URL_PATTERN = originPathPrefix(new URL(`${environment.apiUrl}/user/online/friends`, window.location.href));

const IGNORED_URL_PATTERNS: RegExp[] = [
    new RegExp(`^(?!(?:${BACKEND_URL_PREFIXES}))`),
    new RegExp(`^${ONLINE_FRIENDS_URL_PATTERN}`, 'i'),
];

/**
 * Starts Grafana Faro when environment.faroUrl is set; otherwise does nothing, so no code path
 * below runs and no network traffic is produced. Called from main.ts before Angular bootstraps so
 * bootstrap failures are captured too.
 *
 * The session name is the only identifier attached to telemetry: faro.api.setUser() is never
 * called and nothing here records DOM, keystrokes, or page content.
 */
export function initializeTelemetry(): void {
    if (!environment.faroUrl || faroInstance) {
        return;
    }

    const apiOrigin = new URL(environment.apiUrl, window.location.href).origin;

    faroInstance = initializeFaro({
        url: environment.faroUrl,
        app: {
            name: 'critter-client',
            version: appVersion,
            environment: environment.environmentName,
        },
        instrumentations: [
            ...getWebInstrumentations(),
            new TracingInstrumentation({
                instrumentationOptions: {
                    // A string pattern is compared for exact equality, so the origin has to be a RegExp.
                    // The default only propagates same-origin, which excludes the API in local dev
                    // (client on :4200, API on :59010).
                    propagateTraceHeaderCorsUrls: [new RegExp(`^${escapeForRegExp(apiOrigin)}/`)],
                },
            }),
        ],
        ignoreUrls: IGNORED_URL_PATTERNS,
        // console.log/info/debug output can contain user data; only warnings and errors are captured.
        consoleInstrumentation: {
            disabledLevels: [LogLevel.TRACE, LogLevel.DEBUG, LogLevel.LOG, LogLevel.INFO],
        },
        // Angular's default ErrorHandler prints via console.error, which the console instrumentation
        // would report a second time, stringified, next to TelemetryErrorHandler's structured report.
        ignoreErrors: [/^console\.error: ERROR /],
        beforeSend: (item: TransportItem) => scrubIdentityFromUrls(item),
    });

    // Overrides Faro's own auto-generated session id with the server-minted name (session-name.ts)
    // once it resolves, so the same human-readable name identifies the session in both Faro's own
    // frontend telemetry and the backend traces/logs SessionCorrelationInterceptor tags with it -
    // one name to search by instead of two unrelated ids.
    getSessionName().then(sessionName => {
        if (sessionName) {
            faroInstance?.api.setSession({ id: sessionName });
        }
    });
}

/**
 * Angular routes errors thrown in components and event handlers into its ErrorHandler, so they
 * never reach window.onerror where Faro would see them. Reports them to Faro, then defers to
 * Angular's own handler so console output is unchanged. A no-op for Faro when it is not initialized.
 */
@Injectable()
export class TelemetryErrorHandler extends ErrorHandler {
    override handleError(error: unknown): void {
        if (faroInstance) {
            try {
                faroInstance.api.pushError(error instanceof Error ? error : new Error(String(error)));
            } catch {
                // Reporting must never mask the original error.
            }
        }
        super.handleError(error);
    }
}
